import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  KeyRound, 
  Clock, 
  User, 
  Building2, 
  CheckCircle, 
  XCircle,
  Search,
  Filter,
  Plus,
  Shield,
  AlertCircle,
  QrCode,
  Nfc
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';

const AccessControl = () => {
  const [accessLogs, setAccessLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('logs');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    badgeId: '',
    holderName: '',
    department: '',
    badgeType: 'qrcode',
    expiryDate: ''
  });
  const { toast } = useToast();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddBadge = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // For now, just add to mock data (API not implemented yet)
      toast({
        title: "Badge créé",
        description: `Badge ${formData.badgeType === 'qrcode' ? 'QR Code' : 'NFC'} créé pour ${formData.holderName}`,
      });
      setIsAddDialogOpen(false);
      setFormData({
        badgeId: '',
        holderName: '',
        department: '',
        badgeType: 'qrcode',
        expiryDate: ''
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de créer le badge",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = accessLogs.filter(log => {
    const matchesSearch = log.personName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.badgeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || log.status.toLowerCase() === statusFilter.toLowerCase();
    const matchesSite = siteFilter === 'all' || log.siteId.toString() === siteFilter;
    return matchesSearch && matchesStatus && matchesSite;
  });

  const getStatusColor = (status) => {
    return status === 'Autorisé' ? 'text-green-600' : 'text-red-600';
  };

  const getStatusIcon = (status) => {
    return status === 'Autorisé' ? CheckCircle : XCircle;
  };

  const getStatusVariant = (status) => {
    return status === 'Autorisé' ? 'default' : 'destructive';
  };

  const sites = [...new Set(accessLogs.map(log => ({ id: log.siteId, name: log.siteName })))];

  // Real data - will be loaded from API in the future
  const [badges, setBadges] = useState([]);
  const [accessPoints, setAccessPoints] = useState([]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Contrôle d'accès</h2>
          <p className="text-gray-600">Gestion des accès et surveillance des entrées/sorties</p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Nouveau badge</span>
        </Button>
      </div>

      {/* Add Badge Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Créer un badge d'accès</DialogTitle>
            <DialogDescription>
              Configurez un nouveau badge QR Code ou NFC
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddBadge}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="badgeType">Type de badge *</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, badgeType: 'qrcode'})}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center space-y-2 ${formData.badgeType === 'qrcode' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
                  >
                    <QrCode className="w-8 h-8" />
                    <span className="font-medium">QR Code</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, badgeType: 'nfc'})}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center space-y-2 ${formData.badgeType === 'nfc' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
                  >
                    <Nfc className="w-8 h-8" />
                    <span className="font-medium">NFC</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="badgeId">Numéro de badge *</Label>
                  <Input
                    id="badgeId"
                    name="badgeId"
                    value={formData.badgeId}
                    onChange={handleInputChange}
                    placeholder="BADGE-001"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="holderName">Nom du titulaire *</Label>
                  <Input
                    id="holderName"
                    name="holderName"
                    value={formData.holderName}
                    onChange={handleInputChange}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Département</Label>
                  <Input
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    placeholder="Logistique, Maintenance..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiryDate">Date d'expiration</Label>
                  <Input
                    id="expiryDate"
                    name="expiryDate"
                    type="date"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Badge {formData.badgeType === 'qrcode' ? 'QR Code' : 'NFC'} sélectionné</p>
                    <p>
                      {formData.badgeType === 'qrcode' 
                        ? 'Un QR Code unique sera généré pour ce badge. Peut être scanné avec un smartphone.'
                        : 'Badge NFC pour accès par contact sans fil. Compatible avec les lecteurs NFC.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Création...' : 'Créer le badge'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="logs">Journaux d'accès</TabsTrigger>
          <TabsTrigger value="badges">Gestion des badges</TabsTrigger>
          <TabsTrigger value="points">Points d'accès</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="space-y-6">
          {/* Search and Filters */}
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Rechercher par nom, badge ou département..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="autorisé">Autorisé</SelectItem>
                <SelectItem value="refusé">Refusé</SelectItem>
              </SelectContent>
            </Select>

            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrer par site" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les sites</SelectItem>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={site.id.toString()}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Access Logs */}
          <div className="space-y-4">
            {filteredLogs.map((log) => {
              const StatusIcon = getStatusIcon(log.status);
              return (
                <Card key={log.id} className="hover:shadow-md transition-shadow duration-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <KeyRound className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-3 mb-1">
                            <h3 className="font-semibold text-lg">{log.personName}</h3>
                            <Badge variant={getStatusVariant(log.status)}>
                              {log.status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <Clock className="w-4 h-4" />
                              <span>{log.timestamp}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Building2 className="w-4 h-4" />
                              <span>{log.siteName}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Shield className="w-4 h-4" />
                              <span>Badge: {log.badgeId}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <User className="w-4 h-4" />
                              <span>{log.department}</span>
                            </div>
                          </div>
                          <div className="mt-2 text-sm">
                            <span className="font-medium">Localisation:</span> {log.location} • 
                            <span className="font-medium ml-2">Type:</span> {log.accessType}
                            {log.reason && (
                              <span className="text-red-600 ml-2">• Raison: {log.reason}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <StatusIcon className={`w-6 h-6 ${getStatusColor(log.status)}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="badges" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {badges.map((badge) => (
              <Card key={badge.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{badge.id}</CardTitle>
                    <Badge variant={badge.status === 'Actif' ? 'default' : 'destructive'}>
                      {badge.status}
                    </Badge>
                  </div>
                  <CardDescription>{badge.holder}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Département:</span>
                      <span className="text-sm">{badge.department}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Expire le:</span>
                      <span className="text-sm">{new Date(badge.expires).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button size="sm" variant="outline" className="flex-1">
                      Modifier
                    </Button>
                    <Button 
                      size="sm" 
                      variant={badge.status === 'Actif' ? 'destructive' : 'default'}
                      className="flex-1"
                    >
                      {badge.status === 'Actif' ? 'Désactiver' : 'Activer'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="points" className="space-y-6">
          <div className="space-y-4">
            {accessPoints.map((point) => (
              <Card key={point.id} className="hover:shadow-md transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <KeyRound className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-lg">{point.name}</h3>
                          <Badge variant={point.status === 'Actif' ? 'default' : 'secondary'}>
                            {point.status}
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <Building2 className="w-4 h-4" />
                            <span>{point.site}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4" />
                            <span>Dernière activité: {point.lastActivity}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button size="sm" variant="outline">
                        Configurer
                      </Button>
                      <Button size="sm" variant="outline">
                        Historique
                      </Button>
                      {point.status === 'Maintenance' && (
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccessControl;